import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Container, Row, Col, Card, CardBody, CardHeader,
    Input, Button, Label, Table, Spinner, Alert, Badge,
} from "reactstrap";
import ReactApexChart from "react-apexcharts";
import * as XLSX from "xlsx";
import { DashboardPageHeader, EmptyState } from "../../Components/Common/DashboardKit";
import { getXRayReport } from "../../services/reportsApi";

// ---------------------------------------------------------------------------
// src/pages/KPI/XRayReport.js  (Round 3 — UI pass)
//
// X-Ray management finance report, rebuilt from the SSRS RDL. Data from
// GET /api/reports/xray?selectDate=YYYY-MM-DD (finance-gated backend).
//
// This pass adds a management-friendly layout: KPI summary cards, charts
// (segment mix + backlog by segment + shipment comparison), and cleaner,
// properly-structured tables with right-aligned monospaced figures and a
// highlighted Total column. Data mapping is unchanged.
// ---------------------------------------------------------------------------

const SEGMENTS = [
    { key: "aircraft",   label: "Aircraft" },
    { key: "automotive", label: "Automotive" },
    { key: "flexMed",    label: "Flex Med." },
    { key: "flexOther",  label: "Flex Other" },
    { key: "medical",    label: "Medical" },
    { key: "shukla",     label: "Shukla" },
];

const SEGMENT_COLORS = ["#405189", "#0ab39c", "#f7b84b", "#f06548", "#299cdb", "#4b38b3"];

const money = new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const moneyCompact = new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
    notation: "compact", maximumFractionDigits: 1,
});
const fmt = (v) => money.format(Number(v) || 0);
const fmtC = (v) => moneyCompact.format(Number(v) || 0);

function todayISO() {
    const d = new Date();
    const tz = d.getTimezoneOffset() * 60000;
    return new Date(d - tz).toISOString().slice(0, 10);
}

function findRow(section, fragment) {
    if (!section || !section.rows) return null;
    const f = fragment.toLowerCase();
    return section.rows.find((r) => (r.heading || "").toLowerCase().includes(f)) || null;
}
function sectionByKey(data, key) {
    return (data && data.sections) ? data.sections.find((s) => s.key === key) || null : null;
}

// Date shortcut helpers (return YYYY-MM-DD in local time).
function isoOf(d) {
    const tz = d.getTimezoneOffset() * 60000;
    return new Date(d - tz).toISOString().slice(0, 10);
}
function monthEndISO() {
    const n = new Date();
    return isoOf(new Date(n.getFullYear(), n.getMonth() + 1, 0));
}
function lastMonthEndISO() {
    const n = new Date();
    return isoOf(new Date(n.getFullYear(), n.getMonth(), 0));
}

// Export the full report to a multi-sheet .xlsx (one sheet per section).
function exportToExcel(data) {
    if (!data || !data.sections) return;
    const wb = XLSX.utils.book_new();
    const header = ["Heading", "Description", "Aircraft", "Automotive", "Flex Med.", "Flex Other", "Medical", "Shukla", "Total"];

    data.sections.forEach((s) => {
        const aoa = [header];
        (s.rows || []).forEach((r) => {
            aoa.push([
                r.heading, r.description,
                Number(r.aircraft) || 0, Number(r.automotive) || 0,
                Number(r.flexMed) || 0, Number(r.flexOther) || 0,
                Number(r.medical) || 0, Number(r.shukla) || 0,
                Number(r.total) || 0,
            ]);
        });
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws["!cols"] = [{ wch: 28 }, { wch: 42 }, ...Array(7).fill({ wch: 15 })];
        // Sheet names max 31 chars, no special chars.
        const name = (s.title || s.key).replace(/[\\/?*[\]:]/g, "").slice(0, 31);
        XLSX.utils.book_append_sheet(wb, ws, name);
    });

    // Summary sheet with the date + opening balance.
    const summary = [
        ["X-Ray Report"],
        ["Report date", new Date(data.selectDate).toLocaleDateString()],
        ["Generated", new Date(data.generatedAt).toLocaleString()],
    ];
    if (data.openingBalance != null) {
        summary.push(["Bank opening balance", Number(data.openingBalance) || 0]);
    }
    const sws = XLSX.utils.aoa_to_sheet(summary);
    sws["!cols"] = [{ wch: 22 }, { wch: 26 }];
    XLSX.utils.book_append_sheet(wb, sws, "Summary");

    const dateStr = isoOf(new Date(data.selectDate));
    XLSX.writeFile(wb, `XRay_Report_${dateStr}.xlsx`);
}

const NumCell = ({ value, strong, muted }) => {
    const n = Number(value) || 0;
    const cls = [
        "text-end", "font-monospace",
        strong ? "fw-semibold" : "",
        muted && n === 0 ? "text-muted opacity-50" : "",
        n < 0 ? "text-danger" : "",
    ].join(" ");
    return <td className={cls} style={{ whiteSpace: "nowrap" }}>{fmt(n)}</td>;
};

const SectionTable = ({ section }) => {
    if (section.error) {
        return (
            <Card className="mb-4 shadow-sm border-0">
                <CardHeader className="bg-transparent border-bottom fw-semibold">{section.title}</CardHeader>
                <CardBody>
                    <Alert color="warning" className="mb-0">
                        <i className="ri-error-warning-line me-1" />
                        This section couldn't be loaded. {section.error}
                    </Alert>
                </CardBody>
            </Card>
        );
    }

    const rows = section.rows || [];

    return (
        <Card className="mb-4 shadow-sm border-0">
            <CardHeader className="bg-transparent border-bottom d-flex align-items-center py-3">
                <span className="avatar-xs flex-shrink-0 me-2">
                    <span className="avatar-title bg-primary-subtle text-primary rounded fs-16">
                        <i className="ri-table-2" />
                    </span>
                </span>
                <h5 className="card-title mb-0 flex-grow-1">{section.title}</h5>
                <Badge color="light" className="text-muted">{rows.length} rows</Badge>
            </CardHeader>
            <CardBody className="p-0">
                {rows.length === 0 ? (
                    <div className="p-3">
                        <EmptyState icon="ri-inbox-line" title="No rows for this date" tone="secondary" compact />
                    </div>
                ) : (
                    <div className="table-responsive">
                        <Table className="align-middle table-nowrap mb-0 table-hover">
                            <thead>
                                <tr className="table-light">
                                    <th style={{ minWidth: 200 }}>Heading</th>
                                    <th style={{ minWidth: 260 }}>Description</th>
                                    {SEGMENTS.map((c) => (
                                        <th key={c.key} className="text-end">{c.label}</th>
                                    ))}
                                    <th className="text-end bg-primary-subtle">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => {
                                    const isTotalRow = /total|backlog total|potential|ytd|pace/i.test(r.heading || "");
                                    return (
                                        <tr key={i} className={isTotalRow ? "table-light fw-semibold" : ""}>
                                            <td className="fw-medium text-nowrap">{r.heading}</td>
                                            <td className="text-muted small" style={{ whiteSpace: "normal" }}>{r.description}</td>
                                            {SEGMENTS.map((c) => (
                                                <NumCell key={c.key} value={r[c.key]} muted strong={isTotalRow} />
                                            ))}
                                            <td className="text-end font-monospace fw-bold bg-primary-subtle text-primary"
                                                style={{ whiteSpace: "nowrap" }}>
                                                {fmt(r.total)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}
            </CardBody>
        </Card>
    );
};

const KpiCard = ({ icon, label, value, sub, tone = "primary" }) => (
    <Card className="card-animate shadow-sm border-0 h-100">
        <CardBody>
            <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                    <p className="text-uppercase fw-medium text-muted mb-2 fs-12">{label}</p>
                    <h4 className="fs-20 fw-bold mb-0">{value}</h4>
                    {sub && <p className="text-muted mb-0 mt-1 fs-12">{sub}</p>}
                </div>
                <div className="flex-shrink-0">
                    <span className={`avatar-sm rounded bg-${tone}-subtle d-flex align-items-center justify-content-center`}>
                        <i className={`${icon} fs-20 text-${tone}`} />
                    </span>
                </div>
            </div>
        </CardBody>
    </Card>
);

const ChartsPanel = ({ data }) => {
    const billing = sectionByKey(data, "billing");
    const orders = sectionByKey(data, "orders");
    const shipment = sectionByKey(data, "shipment");

    const potential = findRow(billing, "potential billing");
    const donut = useMemo(() => {
        if (!potential) return null;
        const vals = SEGMENTS.map((s) => Math.max(0, Number(potential[s.key]) || 0));
        if (vals.every((v) => v === 0)) return null;
        return { series: vals, labels: SEGMENTS.map((s) => s.label) };
    }, [potential]);

    const backlog = findRow(orders, "backlog total") || findRow(orders, "backlog");
    const bar = useMemo(() => {
        if (!backlog) return null;
        const vals = SEGMENTS.map((s) => Number(backlog[s.key]) || 0);
        if (vals.every((v) => v === 0)) return null;
        return { series: [{ name: "Backlog", data: vals }], categories: SEGMENTS.map((s) => s.label) };
    }, [backlog]);

    const shipMonth = findRow(shipment, "shipped for last month") || (shipment && shipment.rows ? shipment.rows[0] : null);
    const shipYTD = findRow(shipment, "calendar year: 20") || findRow(shipment, "calendar year");
    const compare = useMemo(() => {
        if (!shipMonth && !shipYTD) return null;
        const series = [];
        if (shipMonth) series.push({ name: (shipMonth.heading || "Last Month").replace(/^\d+\s*/, ""), data: SEGMENTS.map((s) => Number(shipMonth[s.key]) || 0) });
        if (shipYTD) series.push({ name: (shipYTD.heading || "Calendar Year").replace(/^\d+\s*/, ""), data: SEGMENTS.map((s) => Number(shipYTD[s.key]) || 0) });
        if (series.length === 0) return null;
        return { series, categories: SEGMENTS.map((s) => s.label) };
    }, [shipMonth, shipYTD]);

    const moneyAxis = { labels: { formatter: (v) => fmtC(v) } };

    return (
        <Row className="g-3 mb-2">
            {donut && (
                <Col xl={4} md={6}>
                    <Card className="shadow-sm border-0 h-100">
                        <CardHeader className="bg-transparent border-0 pb-0">
                            <h6 className="card-title mb-0">Potential Billing — Segment Mix</h6>
                            <small className="text-muted">Open + projected, by business segment</small>
                        </CardHeader>
                        <CardBody>
                            <ReactApexChart type="donut" height={300} series={donut.series}
                                options={{
                                    labels: donut.labels, colors: SEGMENT_COLORS,
                                    legend: { position: "bottom" }, dataLabels: { enabled: false },
                                    tooltip: { y: { formatter: (v) => fmt(v) } },
                                    plotOptions: { pie: { donut: { size: "62%" } } },
                                }} />
                        </CardBody>
                    </Card>
                </Col>
            )}
            {bar && (
                <Col xl={4} md={6}>
                    <Card className="shadow-sm border-0 h-100">
                        <CardHeader className="bg-transparent border-0 pb-0">
                            <h6 className="card-title mb-0">Order Backlog by Segment</h6>
                            <small className="text-muted">Not yet shipped, all time</small>
                        </CardHeader>
                        <CardBody>
                            <ReactApexChart type="bar" height={300} series={bar.series}
                                options={{
                                    chart: { toolbar: { show: false } }, colors: SEGMENT_COLORS,
                                    plotOptions: { bar: { distributed: true, borderRadius: 4, horizontal: true } },
                                    dataLabels: { enabled: false }, legend: { show: false },
                                    xaxis: { categories: bar.categories, labels: { formatter: (v) => fmtC(v) } },
                                    tooltip: { y: { formatter: (v) => fmt(v) } },
                                }} />
                        </CardBody>
                    </Card>
                </Col>
            )}
            {compare && (
                <Col xl={4} md={12}>
                    <Card className="shadow-sm border-0 h-100">
                        <CardHeader className="bg-transparent border-0 pb-0">
                            <h6 className="card-title mb-0">Shipments by Segment</h6>
                            <small className="text-muted">Period vs calendar-year comparison</small>
                        </CardHeader>
                        <CardBody>
                            <ReactApexChart type="bar" height={300} series={compare.series}
                                options={{
                                    chart: { toolbar: { show: false } }, colors: ["#405189", "#0ab39c"],
                                    plotOptions: { bar: { borderRadius: 4, columnWidth: "60%" } },
                                    dataLabels: { enabled: false }, legend: { position: "top" },
                                    xaxis: { categories: compare.categories }, yaxis: moneyAxis,
                                    tooltip: { y: { formatter: (v) => fmt(v) } },
                                }} />
                        </CardBody>
                    </Card>
                </Col>
            )}
        </Row>
    );
};

const SummaryStrip = ({ data }) => {
    const billing = sectionByKey(data, "billing");
    const orders = sectionByKey(data, "orders");
    const receivables = sectionByKey(data, "receivables");
    const inventory = sectionByKey(data, "inventory");

    const potential = findRow(billing, "potential billing");
    const totalShipped = findRow(billing, "total shipped");
    const backlog = findRow(orders, "backlog total") || findRow(orders, "backlog");
    const arTotal = findRow(receivables, "38 total") || findRow(receivables, "total");
    const onHand = findRow(inventory, "onhand") || findRow(inventory, "current total");

    const cards = [
        potential && { icon: "ri-funds-line", tone: "primary", label: "Potential Billing", value: fmtC(potential.total), sub: "Open + projected this month" },
        totalShipped && { icon: "ri-truck-line", tone: "success", label: "Total Shipped", value: fmtC(totalShipped.total), sub: "Shipped this period" },
        backlog && { icon: "ri-stack-line", tone: "warning", label: "Order Backlog", value: fmtC(backlog.total), sub: "Not yet shipped" },
        arTotal && { icon: "ri-bank-card-line", tone: "info", label: "Receivables", value: fmtC(arTotal.total), sub: "Open invoice balance" },
        onHand && { icon: "ri-archive-line", tone: "secondary", label: "Inventory On Hand", value: fmtC(onHand.total), sub: "Current inventory value" },
        (data && data.openingBalance != null) && { icon: "ri-bank-line", tone: "dark", label: "Bank Opening Balance", value: fmtC(data.openingBalance), sub: "Wells Fargo Revenue acct" },
    ].filter(Boolean);

    if (cards.length === 0) return null;

    return (
        <Row className="g-3 mb-2">
            {cards.map((c, i) => (
                <Col xl={true} md={4} sm={6} key={i}><KpiCard {...c} /></Col>
            ))}
        </Row>
    );
};

// Loading skeleton — shimmering placeholders that mirror the real layout, so
// the page feels responsive while the Kinetic queries run.
const Shimmer = ({ w = "100%", h = 16, className = "" }) => (
    <span
        className={`d-inline-block rounded ${className}`}
        style={{
            width: w, height: h,
            background: "linear-gradient(90deg, #eceef1 25%, #f6f7f9 37%, #eceef1 63%)",
            backgroundSize: "400% 100%",
            animation: "xraySkeleton 1.4s ease infinite",
        }}
    />
);

const SkeletonReport = () => (
    <>
        <style>{`@keyframes xraySkeleton{0%{background-position:100% 50%}100%{background-position:0 50%}}`}</style>
        <Row className="g-3 mb-2">
            {Array.from({ length: 5 }).map((_, i) => (
                <Col xl={true} md={4} sm={6} key={i}>
                    <Card className="shadow-sm border-0 h-100"><CardBody>
                        <Shimmer w="60%" h={10} className="mb-2" />
                        <Shimmer w="80%" h={22} />
                    </CardBody></Card>
                </Col>
            ))}
        </Row>
        <Row className="g-3 mb-2">
            {Array.from({ length: 3 }).map((_, i) => (
                <Col xl={4} md={6} key={i}>
                    <Card className="shadow-sm border-0 h-100"><CardBody>
                        <Shimmer w="50%" h={12} className="mb-3" />
                        <Shimmer w="100%" h={220} />
                    </CardBody></Card>
                </Col>
            ))}
        </Row>
        {Array.from({ length: 3 }).map((_, s) => (
            <Card className="mb-4 shadow-sm border-0" key={s}>
                <CardHeader className="bg-transparent border-bottom py-3">
                    <Shimmer w="220px" h={16} />
                </CardHeader>
                <CardBody>
                    {Array.from({ length: 4 }).map((_, r) => (
                        <div className="d-flex justify-content-between mb-2" key={r}>
                            <Shimmer w="30%" h={14} />
                            <Shimmer w="55%" h={14} />
                        </div>
                    ))}
                </CardBody>
            </Card>
        ))}
    </>
);

const XRayReport = () => {
    document.title = "X-Ray Report | SSW Technologies Dashboard";

    const [selectDate, setSelectDate] = useState(todayISO());
    const [pendingDate, setPendingDate] = useState(todayISO());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async (dateStr) => {
        setLoading(true);
        setError(null);
        try {
            const res = await getXRayReport(dateStr);
            setData(res);
        } catch (e) {
            const status = e && e.response ? e.response.status : null;
            setError(status === 401 || status === 403
                ? "You don't have access to this finance report."
                : "Couldn't load the X-Ray report. Please try again.");
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(selectDate);
    }, [load, selectDate]);

    const applyDate = () => setSelectDate(pendingDate);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <DashboardPageHeader
                        icon="ri-radar-line"
                        eyebrow="Finance"
                        title="X-Ray Report"
                        subtitle="Management finance health — billing, shipments, orders, inventory, bookings, receivables"
                        tone="violet"
                        breadcrumb={[
                            { label: "Dashboards", to: "/" },
                            { label: "KPI", to: "/kpi" },
                            { label: "X-Ray Report" },
                        ]}
                    />

                    <Card className="mb-4 shadow-sm border-0">
                        <CardBody>
                            <Row className="g-2 align-items-end">
                                <Col xs="auto">
                                    <Label htmlFor="xray-date" className="form-label mb-1 fw-medium">
                                        <i className="ri-calendar-event-line me-1" /> Select Date
                                    </Label>
                                    <Input id="xray-date" type="date" value={pendingDate}
                                        onChange={(e) => setPendingDate(e.target.value)} style={{ maxWidth: 200 }} />
                                </Col>
                                <Col xs="auto">
                                    <Button color="primary" onClick={applyDate} disabled={loading}>
                                        {loading
                                            ? <><Spinner size="sm" className="me-1" /> Loading…</>
                                            : <><i className="ri-refresh-line me-1" /> Apply</>}
                                    </Button>
                                </Col>
                                <Col xs="auto">
                                    <div className="btn-group" role="group" aria-label="Date shortcuts">
                                        <Button color="light" size="sm" onClick={() => { setPendingDate(todayISO()); setSelectDate(todayISO()); }}>
                                            Today
                                        </Button>
                                        <Button color="light" size="sm" onClick={() => { const d = monthEndISO(); setPendingDate(d); setSelectDate(d); }}>
                                            Month End
                                        </Button>
                                        <Button color="light" size="sm" onClick={() => { const d = lastMonthEndISO(); setPendingDate(d); setSelectDate(d); }}>
                                            Last Month
                                        </Button>
                                    </div>
                                </Col>
                                <Col xs="auto">
                                    <Button color="success" outline onClick={() => exportToExcel(data)} disabled={loading || !data}>
                                        <i className="ri-file-excel-2-line me-1" /> Excel
                                    </Button>
                                </Col>
                                <Col xs="auto">
                                    <Button color="light" onClick={() => window.print()} disabled={loading || !data}>
                                        <i className="ri-printer-line me-1" /> Print
                                    </Button>
                                </Col>
                                {data && data.generatedAt && (
                                    <Col xs="auto" className="ms-auto text-muted fs-12 text-end">
                                        <div>Report date: <span className="fw-medium">{new Date(data.selectDate).toLocaleDateString()}</span></div>
                                        <div>Generated {new Date(data.generatedAt).toLocaleString()}</div>
                                    </Col>
                                )}
                            </Row>
                        </CardBody>
                    </Card>

                    {error && (
                        <Alert color="danger"><i className="ri-error-warning-line me-1" /> {error}</Alert>
                    )}

                    {loading && !data && <SkeletonReport />}

                    {data && (
                        <>
                            <SummaryStrip data={data} />
                            <ChartsPanel data={data} />
                            {data.sections && data.sections.map((s) => (
                                <SectionTable key={s.key} section={s} />
                            ))}
                        </>
                    )}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default XRayReport;
